import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import { LocalStorageService } from 'api';

const loginEp = 'auth/login';
const version = '/v1';
const linked_accounts = 'is_linked';
const user_details = 'user';
const member_types = 'member_types';
const discount_codes = 'discount_codes';
const member_notifications = 'memberemail_forms';
const member_sign_up = 'membersignup_forms';
const gated_content = 'gated_content';
const directory_settings = 'directory_settings';
const directory_code = 'directory_code';
const changePass = 'password/change';
const plans = 'plans';
const user_stats = 'user_stats';
const sign_up = 'create/user';
const stripe_keys = 'st/key';
const payments = 'payments';
const link_hubspot = 'link_hubspot';
const link_square = 'link_square';
const renew_member = 'renew_member';
const form_token = 'form_token';
const test_email = 'test_email';
const getCurrencies = 'get_currencies';
const domain_auth = 'sendgrid/domain_auth';
const validate_domain = 'sendgrid/validate_domain';
const memberemail_cc = 'memberemail_cc';

const localStorageService = new LocalStorageService();

const TAGS = {
	USER: 'USER',
	MEMBER_TYPE: 'MEMBER_TYPE',
	DISCOUNT_CODE: 'DISCOUNT_CODE',
	MEMBER_NOTIFICATIONS: 'MEMBER_NOTIFICATIONS',
	MEMBER_SIGN_UP_FORM: 'MEMBER_SIGN_UP_FORM',
	GATED_CONTENT: 'GATED_CONTENT',
	DIRECTORY_SETTINGS: 'DIRECTORY_SETTINGS',
	PAYMENTS: 'PAYMENTS'
};

export const versionAPI = createApi({
	reducerPath: 'versionAPI',
	tagTypes: [
		TAGS.USER,
		TAGS.MEMBER_TYPE,
		TAGS.DISCOUNT_CODE,
		TAGS.MEMBER_NOTIFICATIONS,
		TAGS.MEMBER_SIGN_UP_FORM,
		TAGS.GATED_CONTENT,
		TAGS.DIRECTORY_SETTINGS,
		TAGS.PAYMENTS
	],
	baseQuery: fetchBaseQuery({
		baseUrl: `${version}/`,
		prepareHeaders: headers => {
			const token = localStorageService.getAccessToken();
			if (token) {
				headers.set('authorization', `Bearer ${token}`);
			}

			return headers;
		}
	}),

	endpoints: builder => ({
		getUser: builder.query({
			providesTags: [TAGS.USER],
			query: () => 'user/get_user'
		}),
		getUserStatistic: builder.query({
			query: () => 'user/stats'
		}),
		login: builder.mutation({
			query: ({ username, password }) => ({
				url: `${loginEp}/`,
				body: {
					email: username,
					password
				},
				method: 'POST'
			})
		}),
		signup: builder.mutation({
			query: payload => ({
				url: `${sign_up}/`,
				body: payload,
				method: 'POST'
			})
		}),
		getLinkedAccounts: builder.query({
			query: () => `${linked_accounts}/`
		}),
		editUser: builder.mutation({
			query: payload => ({
				url: `${user_details}/`,
				body: payload,
				method: 'PATCH'
			}),
			invalidatesTags: [TAGS.USER]
		}),
		getMemberTypes: builder.query({
			query: () => `${member_types}/`,
			providesTags: [TAGS.MEMBER_TYPE]
		}),
		deleteMemberType: builder.mutation({
			query: id => ({
				url: `${member_types}/${id}/`,
				method: 'DELETE'
			}),
			invalidatesTags: [TAGS.MEMBER_TYPE]
		}),
		editMemberType: builder.mutation({
			query: ({ id, payload }) => {
				return {
					url: `${member_types}/${id}/`,
					body: payload,
					method: 'PATCH'
				};
			},
			invalidatesTags: [TAGS.MEMBER_TYPE]
		}),
		addMemberType: builder.mutation({
			query: payload => ({
				url: `${member_types}/`,
				body: payload,
				method: 'POST'
			}),
			invalidatesTags: [TAGS.MEMBER_TYPE]
		}),
		getDiscountCodes: builder.query({
			query: () => `${discount_codes}/`,
			providesTags: [TAGS.DISCOUNT_CODE]
		}),
		deleteDiscountCode: builder.mutation({
			query: id => ({
				url: `${discount_codes}/${id}/`,
				method: 'DELETE'
			}),
			invalidatesTags: [TAGS.DISCOUNT_CODE]
		}),
		editDiscountCode: builder.mutation({
			query: ({ id, payload }) => {
				return {
					url: `${discount_codes}/${id}/`,
					body: payload,
					method: 'PATCH'
				};
			},
			invalidatesTags: [TAGS.DISCOUNT_CODE]
		}),
		addDiscountCode: builder.mutation({
			query: payload => ({
				url: `${discount_codes}/`,
				body: payload,
				method: 'POST'
			}),
			invalidatesTags: [TAGS.DISCOUNT_CODE]
		}),
		getMemberNotifications: builder.query({
			query: () => `${member_notifications}/`,
			providesTags: [TAGS.MEMBER_NOTIFICATIONS]
		}),
		getMemberNotificationById: builder.query({
			query: id => `${member_notifications}/${id}`,
			// TODO: refactor this in the future.. Tag must be related to notification object
			providesTags: [TAGS.MEMBER_NOTIFICATIONS]
		}),
		editMemberNotification: builder.mutation({
			query: ({ id, payload }) => ({
				url: `${member_notifications}/${id}/`,
				body: payload,
				method: 'PATCH'
			}),
			invalidatesTags: [TAGS.MEMBER_NOTIFICATIONS]
		}),
		addMemberNotification: builder.mutation({
			query: payload => ({
				url: `${member_notifications}/`,
				body: payload,
				method: 'POST'
			}),
			invalidatesTags: [TAGS.MEMBER_NOTIFICATIONS]
		}),
		deleteMemberNotification: builder.mutation({
			query: id => ({
				url: `${member_notifications}/${id}/`,
				method: 'DELETE'
			}),
			invalidatesTags: [TAGS.MEMBER_NOTIFICATIONS]
		}),
		getMemberSignUpForm: builder.query({
			query: () => `${member_sign_up}/`,
			providesTags: [TAGS.MEMBER_SIGN_UP_FORM]
		}),
		editMemberSignUpForm: builder.mutation({
			query: ({ id, payload, successUrl, memberInfoUrl }) => ({
				url: `${member_sign_up}/${id}/`,
				body: {
					form_options: payload,
					success_url: successUrl,
					member_info_url: memberInfoUrl
				},
				method: 'PATCH'
			}),
			invalidatesTags: [TAGS.MEMBER_SIGN_UP_FORM]
		}),
		createMemberSignUpForm: builder.mutation({
			query: ({ payload, successUrl, memberInfoUrl }) => ({
				url: `${member_sign_up}/`,
				body: {
					form_options: payload,
					success_url: successUrl,
					member_info_url: memberInfoUrl
				},
				method: 'POST'
			}),
			invalidatesTags: [TAGS.MEMBER_SIGN_UP_FORM]
		}),
		getGatedContent: builder.query({
			query: () => `${gated_content}/`,
			providesTags: [TAGS.GATED_CONTENT]
		}),
		getDirectorySettings: builder.query({
			query: () => `${directory_settings}/`,
			providesTags: [TAGS.DIRECTORY_SETTINGS]
		}),
		editDirectorySettings: builder.mutation({
			query: ({ id, payload }) => ({ url: `${directory_settings}/${id}/`, body: payload, method: 'PUT' }),
			invalidatesTags: [TAGS.DIRECTORY_SETTINGS]
		}),
		getDirectoryCode: builder.query({
			query: () => `${directory_code}/`
		}),
		changePassword: builder.mutation({
			query: payload => ({
				url: `${changePass}/`,
				body: {
					new_password1: payload.password1,
					new_password2: payload.password2
				},
				method: 'POST'
			})
		}),
		getMembercenterPlans: builder.query({
			query: () => `${plans}/`
		}),
		getUserStats: builder.query({
			query: () => `${user_stats}/`
		}),
		getStripeKeys: builder.query({
			query: () => `${stripe_keys}/`
		}),
		getUserPayments: builder.query({
			query: () => `${payments}/`,
			providesTags: [TAGS.PAYMENTS]
		}),
		updateMemberCenterSubscription: builder.mutation({
			query: data => ({
				url: `${payments}/`,
				body: data,
				method: 'POST'
			}),
			invalidatesTags: [TAGS.PAYMENTS, TAGS.USER]
		}),
		getHubspotLink: builder.query({
			query: () => `${link_hubspot}/`
		}),
		getSquareLink: builder.query({
			query: () => `${link_square}/`
		}),
		getRenewInformation: builder.query({
			query: ({ portalId, contactId }) => `/${renew_member}/${portalId}/${contactId}`
		}),
		getFormToken: builder.query({
			query: () => `${form_token}/`
		}),
		renewSubscriptionWithCardOnFile: builder.mutation({
			query: ({ portalId, contactId, data }) => ({
				url: `${renew_member}/${portalId}/${contactId}`,
				body: data,
				method: 'POST'
			})
		}),
		sendTestEmail: builder.mutation({
			query: data => {
				return {
					url: `${test_email}/`,
					body: data,
					method: 'POST'
				};
			}
		}),
		getCurrencies: builder.query({
			query: () => `${getCurrencies}/`
		}),
		getDomainAuth: builder.query({
			query: () => `${domain_auth}/`
		}),
		verifyDomain: builder.mutation({
			query: () => ({
				url: `${domain_auth}/`,
				method: 'POST'
			})
		}),
		validateDomain: builder.query({
			query: () => `${validate_domain}/`,
			keepUnusedDataFor: 0
		}),
		createEmailCarbonCopy: builder.mutation({
			query: body => ({ url: `${memberemail_cc}/`, body, method: 'POST' }),
			invalidatesTags: [TAGS.MEMBER_NOTIFICATIONS]
		}),
		deleteEmailCarbonCopy: builder.mutation({
			query: id => ({ url: `${memberemail_cc}/${id}/`, method: 'DELETE' })
		})
	})
});

export const {
	useGetUserQuery,
	useLazyGetUserQuery,
	useLoginMutation,
	useGetLinkedAccountsQuery,
	useEditUserMutation,
	useGetMemberTypesQuery,
	useDeleteMemberTypeMutation,
	useEditMemberTypeMutation,
	useAddMemberTypeMutation,
	useGetDiscountCodesQuery,
	useDeleteDiscountCodeMutation,
	useEditDiscountCodeMutation,
	useAddDiscountCodeMutation,
	useGetMemberNotificationsQuery,
	useEditMemberNotificationMutation,
	useAddMemberNotificationMutation,
	useDeleteMemberNotificationMutation,
	useGetMemberSignUpFormQuery,
	useEditMemberSignUpFormMutation,
	useGetGatedContentQuery,
	useGetDirectorySettingsQuery,
	useGetDirectoryCodeQuery,
	useEditDirectorySettingsMutation,
	useChangePasswordMutation,
	useGetMembercenterPlansQuery,
	useGetUserStatsQuery,
	useSignupMutation,
	useLazyGetStripeKeysQuery,
	useCreateMemberSignUpFormMutation,
	useGetUserPaymentsQuery,
	useUpdateMemberCenterSubscriptionMutation,
	useLazyGetHubspotLinkQuery,
	useLazyGetSquareLinkQuery,
	useGetRenewInformationQuery,
	useGetFormTokenQuery,
	useRenewSubscriptionWithCardOnFileMutation,
	useSendTestEmailMutation,
	useGetCurrenciesQuery,
	useGetDomainAuthQuery,
	useVerifyDomainMutation,
	useValidateDomainQuery,
	useCreateEmailCarbonCopyMutation,
	useDeleteEmailCarbonCopyMutation,
	useGetMemberNotificationByIdQuery
} = versionAPI;
